import {
  ActionIcon,
  Badge,
  Code,
  Group,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCode, IconKey, IconTrash } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { WorkspaceApiKey } from '@/types/workspace'
import { getFormattedDate } from '@/utils/date'
import { ApiKeyExampleDrawer } from './ApiKeyExampleDrawer'
import { DeleteApiKeyModal } from './DeleteApiKeyModal'
import { getScopeConfig } from './apiKeyUtils'

type ApiKeyListItemProps = {
  apiKey: WorkspaceApiKey
}

export const ApiKeyListItem = ({ apiKey }: ApiKeyListItemProps) => {
  const [exampleOpened, { open: openExample, close: closeExample }] =
    useDisclosure(false)

  const [deleteOpened, { open: openDelete, close: closeDelete }] =
    useDisclosure(false)

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <IconKey size={20} />
        </Table.Td>

        <Table.Td>
          <Stack gap="xs" align="flex-start">
            <Text size="sm" fw={600}>
              {apiKey.name}
            </Text>
            <Code fz={11}>{apiKey.key_masked}</Code>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Code fz={12}>{getScopeConfig(apiKey.scope).endpoint}</Code>
        </Table.Td>

        <Table.Td>
          {apiKey.applications?.length ? (
            <Group gap="xs">
              {apiKey.applications.map((app) => (
                <Badge key={app.id} size="sm" variant="default">
                  {app.name}
                </Badge>
              ))}
            </Group>
          ) : (
            '-'
          )}
        </Table.Td>

        <Table.Td>{apiKey.created_by.full_name}</Table.Td>

        <Table.Td>
          {getFormattedDate(apiKey.created_at, { month: 'short' })}
        </Table.Td>

        <Table.Td>
          <Group gap="xs">
            <Tooltip
              label={<FormattedMessage id="workspace.apiKeys.showExample" />}
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                radius="xl"
                onClick={openExample}
              >
                <IconCode style={{ width: '80%', height: '80%' }} />
              </ActionIcon>
            </Tooltip>

            <Tooltip
              label={<FormattedMessage id="workspace.apiKeys.delete.title" />}
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                radius="xl"
                onClick={openDelete}
              >
                <IconTrash style={{ width: '80%', height: '80%' }} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>

      <ApiKeyExampleDrawer
        apiKey={apiKey}
        opened={exampleOpened}
        onClose={closeExample}
      />

      <DeleteApiKeyModal
        apiKey={apiKey}
        opened={deleteOpened}
        onClose={closeDelete}
      />
    </>
  )
}
